import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import useAuth from "../../hooks/useAuth";
import SelectList from "../../components/SelectList/SelectList";
import "./AdminDashboardPage.css";

const API = (process.env.REACT_APP_API_URL || "") + "/api/receipts";
const displayName = c => {
  const leaf = c.name.includes(':') ? c.name.split(':').pop().trim() : c.name;
  return c.acct_num ? `${c.acct_num} ${leaf}` : leaf;
};
const displayAccountName = name => {
  if (!name) return name;
  const idx = name.lastIndexOf(':');
  return idx >= 0 ? name.slice(idx + 1).trim() : name;
};
const AUTH_API = (process.env.REACT_APP_API_URL || "") + "/api/auth";

export default function AdminDashboardPage() {
  const [, token] = useAuth();
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };
  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState(location.state?.tab || "submissions");

  // Submissions
  const [submissions, setSubmissions] = useState([]);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [approving, setApproving] = useState({});         // { [id]: true }
  const [matchResults, setMatchResults] = useState({});   // { [id]: result }
  const [selected, setSelected] = useState({});           // { [id]: true } for batch
  const [batchApproving, setBatchApproving] = useState(false);
  const [batchResults, setBatchResults] = useState(null);
  const [batchDeleting, setBatchDeleting] = useState(false);
  const [batchDeleteError, setBatchDeleteError] = useState("");

  // Users
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [newUser, setNewUser] = useState({ username: "", email: "", first_name: "", last_name: "", password: "", role: "user", group_id: "" });
  const [userMsg, setUserMsg] = useState("");
  const [editingUser, setEditingUser] = useState(null); // { id, first_name, last_name, email }

  // Accounts
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [newAccount, setNewAccount] = useState({ name: "", qbo_id: "", account_type: "" });
  const [accountMsg, setAccountMsg] = useState("");

  // Cards
  const [cards, setCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [newCard, setNewCard] = useState({ last_four: "", account: "", user: "" });
  const [cardMsg, setCardMsg] = useState("");
  const [editingCard, setEditingCard] = useState(null);

  // Submission view/edit panel
  const [viewingSubmission, setViewingSubmission] = useState(null);
  const [replacingFile, setReplacingFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editLineItems, setEditLineItems] = useState([]);
  const [editSaving, setEditSaving] = useState(false);
  const [editMsg, setEditMsg] = useState("");

  // Reference data for edit form dropdowns (loaded on first edit open)
  const [refVendors, setRefVendors] = useState([]);
  const [refCategories, setRefCategories] = useState([]);
  const [refClasses, setRefClasses] = useState([]);
  const [refLoaded, setRefLoaded] = useState(false);

  // Settings tab — receipt form preferences (admin's personal prefs)
  const [prefAllCategories, setPrefAllCategories] = useState([]);
  const [prefAllClasses, setPrefAllClasses] = useState([]);
  const [prefCategoryIds, setPrefCategoryIds] = useState(null);
  const [prefClassIds, setPrefClassIds] = useState(null);
  const [prefSaving, setPrefSaving] = useState(false);
  const [prefMsg, setPrefMsg] = useState("");

  // Settings tab — purchase filters
  const [filters, setFilters] = useState([]);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [filterMsg, setFilterMsg] = useState("");
  const [filterCategories, setFilterCategories] = useState([]);

  // Settings tab — match settings
  const [matchSettings, setMatchSettings] = useState({ date_window_days: 3, amount_tolerance: "0.01", pool_window_days: 45 });
  const [matchSettingsMsg, setMatchSettingsMsg] = useState("");
  const [matchSettingsSaving, setMatchSettingsSaving] = useState(false);

  // Settings tab — TOTP management
  const [totpStatus, setTotpStatus] = useState(null); // { qr_code, secret, totp_enabled }
  const [totpCode, setTotpCode] = useState("");
  const [totpMsg, setTotpMsg] = useState("");

  // QBO sync
  const [syncMsg, setSyncMsg] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [qboConnected, setQboConnected] = useState(null);
  const [connectUrl, setConnectUrl] = useState("");

  // Submitted / match records tab
  const [matches, setMatches] = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(false);

  // Credentials modal
  const [showCredModal, setShowCredModal] = useState(false);
  const [credForm, setCredForm] = useState({ client_id: "", client_secret: "", redirect_uri: "" });
  const [credStatus, setCredStatus] = useState(null); // { has_credentials, client_id_set, client_secret_set, redirect_uri }
  const [credSaving, setCredSaving] = useState(false);
  const [credMsg, setCredMsg] = useState("");

  // Match result shown inside the view panel
  const [viewMatchResult, setViewMatchResult] = useState(null); // result from approve

  // Bank CSV upload
  const [csvFile, setCsvFile] = useState(null);
  const [csvPreviewRows, setCsvPreviewRows] = useState(null); // editable rows
  const [csvCounts, setCsvCounts] = useState(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvMsg, setCsvMsg] = useState("");
  const [csvForceCardId, setCsvForceCardId] = useState("");
  const [csvImportAll, setCsvImportAll] = useState(false);
  const [csvDefaultAccountId, setCsvDefaultAccountId] = useState("");

  // Reports tab
  const defaultReportMsg = "We are missing receipts for the following transactions from last month. Please submit your receipt(s) as soon as possible through GC Receipts.";
  const [reportMonth, setReportMonth] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().slice(0, 7);
  });
  const [reportMessage, setReportMessage] = useState(defaultReportMsg);
  const [reportPreview, setReportPreview] = useState(true);
  const [reportDrafts, setReportDrafts] = useState(null);   // array of { name, email, subject, body, count, total }
  const [reportNoEmail, setReportNoEmail] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportMsg, setReportMsg] = useState("");
  const [expandedEmail, setExpandedEmail] = useState(null); // index of expanded preview

  // Invite modal
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", username: "", role: "user" });
  const [inviteMsg, setInviteMsg] = useState("");
  const [inviteSending, setInviteSending] = useState(false);
  const [welcomeSending, setWelcomeSending] = useState({});

  // Groups tab
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupMsg, setGroupMsg] = useState("");
  const [newGroupName, setNewGroupName] = useState("");
  const [editingGroup, setEditingGroup] = useState(null);
  const [allRefCategories, setAllRefCategories] = useState([]);
  const [allRefClasses, setAllRefClasses] = useState([]);
  const [globalPref, setGlobalPref] = useState(null);
  const [globalPrefSaving, setGlobalPrefSaving] = useState(false);
  const [globalPrefMsg, setGlobalPrefMsg] = useState("");
  const [editingGlobal, setEditingGlobal] = useState(false);

  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRef = useRef(null);
  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpenMenuId(null);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (activeTab === "submissions") loadSubmissions();
    if (activeTab === "users") { loadUsers(); loadGroups(); }
    if (activeTab === "accounts") { loadAccounts(); loadUsers(); }
    if (activeTab === "cards") { loadCards(); loadAccounts(); loadUsers(); }
    if (activeTab === "csv") { loadCards(); loadAccounts(); loadUsers(); }
    if (activeTab === "submitted") loadMatches();
    if (activeTab === "sync") { loadQBOStatus(); loadCredStatus(); }
    if (activeTab === "settings") { loadFilters(); loadMatchSettings(); loadAdminPreferences(); }
    if (activeTab === "reports") { setReportDrafts(null); setReportMsg(""); }
    if (activeTab === "groups") loadGroups();
  }, [activeTab]);

  const loadQBOStatus = () => {
    axios.get(`${API}/admin/qbo-connect/`, authHeader)
      .then(res => { setQboConnected(res.data.connected); setConnectUrl(res.data.auth_url); })
      .catch(() => setQboConnected(false));
  };

  const loadCredStatus = () => {
    axios.get(`${API}/admin/credentials/`, authHeader)
      .then(res => setCredStatus(res.data))
      .catch(console.error);
  };

  const saveCredentials = () => {
    setCredSaving(true);
    setCredMsg("");
    axios.post(`${API}/admin/credentials/`, credForm, authHeader)
      .then(() => {
        setCredMsg("Credentials saved.");
        setShowCredModal(false);
        loadCredStatus();
        loadQBOStatus();
      })
      .catch(err => setCredMsg(JSON.stringify(err.response?.data) || "Save failed."))
      .finally(() => setCredSaving(false));
  };

  // ── Purchase filters ─────────────────────────────────────────────────────────

  const loadFilters = () => {
    setFiltersLoading(true);
    axios.get(`${API}/admin/purchase-filters/`, authHeader)
      .then(res => setFilters(res.data))
      .catch(console.error)
      .finally(() => setFiltersLoading(false));
    if (filterCategories.length === 0) {
      axios.get(`${API}/reference/`, authHeader)
        .then(res => setFilterCategories(res.data.categories || []))
        .catch(console.error);
    }
  };

  const addFilter = () => {
    setFilters(prev => [...prev, { _new: true, field_name: 'DocNumber', operator: '=', value: '', is_active: true, order: prev.length }]);
  };

  const updateLocalFilter = (index, field, value) => {
    setFilters(prev => prev.map((f, i) => i === index ? { ...f, [field]: value } : f));
  };

  const saveFilter = async (index) => {
    const f = filters[index];
    setFilterMsg("");
    try {
      if (f._new) {
        const res = await axios.post(`${API}/admin/purchase-filters/`, { field_name: f.field_name, operator: f.operator, value: f.value, is_active: f.is_active, order: f.order }, authHeader);
        setFilters(prev => prev.map((x, i) => i === index ? res.data : x));
      } else {
        const res = await axios.patch(`${API}/admin/purchase-filters/${f.id}/`, { field_name: f.field_name, operator: f.operator, value: f.value, is_active: f.is_active }, authHeader);
        setFilters(prev => prev.map((x, i) => i === index ? res.data : x));
      }
      setFilterMsg("Saved.");
    } catch (err) {
      setFilterMsg("Save failed: " + JSON.stringify(err.response?.data));
    }
  };

  const deleteFilter = async (index) => {
    const f = filters[index];
    if (f._new) { setFilters(prev => prev.filter((_, i) => i !== index)); return; }
    if (!window.confirm("Remove this filter?")) return;
    await axios.delete(`${API}/admin/purchase-filters/${f.id}/`, authHeader).catch(console.error);
    setFilters(prev => prev.filter((_, i) => i !== index));
  };

  const resetFilters = async () => {
    if (!window.confirm("Reset to default (DocNumber = 'MISSING RECEIPT')? This removes all custom filters.")) return;
    await axios.post(`${API}/admin/purchase-filters/reset/`, {}, authHeader).catch(console.error);
    loadFilters();
    setFilterMsg("Reset to default.");
  };

  // ── Match settings ───────────────────────────────────────────────────────────

  const loadMatchSettings = () => {
    axios.get(`${API}/admin/match-settings/`, authHeader)
      .then(res => setMatchSettings(res.data))
      .catch(console.error);
  };

  const saveMatchSettings = () => {
    setMatchSettingsSaving(true);
    setMatchSettingsMsg("");
    axios.patch(`${API}/admin/match-settings/`, matchSettings, authHeader)
      .then(res => { setMatchSettings(res.data); setMatchSettingsMsg("Saved."); })
      .catch(err => setMatchSettingsMsg("Save failed: " + JSON.stringify(err.response?.data)))
      .finally(() => setMatchSettingsSaving(false));
  };

  // ── TOTP ─────────────────────────────────────────────────────────────────────

  const loadAdminPreferences = () => {
    axios.get(`${API}/user/preferences/`, authHeader)
      .then(res => {
        setPrefAllCategories(res.data.all_categories || []);
        setPrefAllClasses(res.data.all_classes || []);
        const groupCats = res.data.group_category_ids || [];
        const groupCls = res.data.group_class_ids || [];
        const globalCats = res.data.global_category_ids || [];
        const globalCls = res.data.global_class_ids || [];
        setPrefCategoryIds(
          res.data.category_ids.length > 0 ? res.data.category_ids
          : groupCats.length > 0 ? groupCats
          : globalCats.length > 0 ? globalCats
          : null
        );
        setPrefClassIds(
          res.data.class_ids.length > 0 ? res.data.class_ids
          : groupCls.length > 0 ? groupCls
          : globalCls.length > 0 ? globalCls
          : null
        );
      })
      .catch(console.error);
  };

  const saveAdminPreferences = () => {
    setPrefSaving(true);
    setPrefMsg("");
    axios.patch(`${API}/user/preferences/`, {
      category_ids: prefCategoryIds ?? [],
      class_ids: prefClassIds ?? [],
    }, authHeader)
      .then(() => setPrefMsg("Preferences saved."))
      .catch(() => setPrefMsg("Failed to save."))
      .finally(() => setPrefSaving(false));
  };

  const togglePrefItem = (id, ids, setIds, allItems) => {
    if (ids === null) {
      setIds(allItems.map(i => i.id).filter(i => i !== id));
    } else if (ids.includes(id)) {
      const next = ids.filter(i => i !== id);
      setIds(next.length === 0 ? null : next);
    } else {
      const next = [...ids, id];
      setIds(next.length === allItems.length ? null : next);
    }
  };

  const loadTotpSetup = () => {
    setTotpMsg("");
    axios.get(`${AUTH_API}/totp/setup/`, authHeader)
      .then(res => setTotpStatus(res.data))
      .catch(console.error);
  };

  const confirmTotpSetup = () => {
    setTotpMsg("");
    axios.post(`${AUTH_API}/totp/setup/`, { code: totpCode }, authHeader)
      .then(() => { setTotpMsg("2FA enabled successfully."); loadTotpSetup(); setTotpCode(""); })
      .catch(() => setTotpMsg("Invalid code. Please try again."));
  };

  const loadMatches = () => {
    setMatchesLoading(true);
    axios.get(`${API}/admin/matches/`, authHeader)
      .then(res => setMatches(res.data))
      .catch(console.error)
      .finally(() => setMatchesLoading(false));
  };

  const loadSubmissions = () => {
    setSubmissionsLoading(true);
    setMatchResults({});
    setSelected({});
    setBatchResults(null);
    axios.get(`${API}/submissions/`, authHeader)
      .then(res => setSubmissions(res.data.filter(s => s.status !== 'submitted' && s.status !== 'posted')))
      .catch(console.error)
      .finally(() => setSubmissionsLoading(false));
  };

  const loadUsers = () => {
    setUsersLoading(true);
    axios.get(`${API}/admin/users/`, authHeader)
      .then(res => setUsers(res.data))
      .catch(console.error)
      .finally(() => setUsersLoading(false));
  };

  const loadGroups = () => {
    setGroupsLoading(true);
    axios.get(`${API}/admin/groups/`, authHeader)
      .then(res => setGroups(res.data))
      .catch(console.error)
      .finally(() => setGroupsLoading(false));
    axios.get(`${API}/admin/global-preference/`, authHeader)
      .then(res => {
        setGlobalPref(res.data);
        setAllRefCategories(res.data.all_categories || []);
        setAllRefClasses(res.data.all_classes || []);
      })
      .catch(console.error);
  };

  const createGroup = async (e) => {
    e.preventDefault();
    setGroupMsg("");
    try {
      const res = await axios.post(`${API}/admin/groups/`, { name: newGroupName }, authHeader);
      setGroups(g => [...g, res.data]);
      setNewGroupName("");
      setGroupMsg("Group created.");
    } catch (err) {
      setGroupMsg(err.response?.data?.error || "Failed to create group.");
    }
  };

  const deleteGroup = async (id, name) => {
    if (!window.confirm(`Delete group "${name}"? Users in this group will lose their defaults.`)) return;
    await axios.delete(`${API}/admin/groups/${id}/`, authHeader).catch(console.error);
    setGroups(g => g.filter(x => x.id !== id));
    if (editingGroup?.id === id) setEditingGroup(null);
  };

  const saveGroupPrefs = async () => {
    if (!editingGroup) return;
    setGroupMsg("");
    try {
      const res = await axios.patch(`${API}/admin/groups/${editingGroup.id}/`, {
        category_ids: editingGroup.category_ids,
        class_ids: editingGroup.class_ids,
      }, authHeader);
      setGroups(g => g.map(x => x.id === res.data.id ? res.data : x));
      setEditingGroup(null);
      setGroupMsg(`"${res.data.name}" saved.`);
    } catch (err) {
      setGroupMsg("Save failed.");
    }
  };

  const saveGlobalPref = async () => {
    if (!globalPref) return;
    setGlobalPrefSaving(true);
    setGlobalPrefMsg("");
    try {
      const res = await axios.patch(`${API}/admin/global-preference/`, {
        category_ids: globalPref.category_ids,
        class_ids: globalPref.class_ids,
      }, authHeader);
      setGlobalPref(gp => ({ ...gp, ...res.data }));
      setEditingGlobal(false);
      setGlobalPrefMsg("Global defaults saved.");
    } catch (err) {
      setGlobalPrefMsg("Save failed.");
    } finally {
      setGlobalPrefSaving(false);
    }
  };

  const toggleGlobalItem = (id, field) => {
    setGlobalPref(gp => {
      const ids = gp[field];
      return { ...gp, [field]: ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id] };
    });
  };

  const toggleGroupItem = (id, field) => {
    setEditingGroup(eg => {
      const ids = eg[field];
      return { ...eg, [field]: ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id] };
    });
  };

  const loadAccounts = () => {
    setAccountsLoading(true);
    axios.get(`${API}/admin/accounts/`, authHeader)
      .then(res => setAccounts(res.data))
      .catch(console.error)
      .finally(() => setAccountsLoading(false));
  };

  // ── Submission actions ──────────────────────────────────────────────────────

  const approveSubmission = (id, purchaseId = null) => {
    setApproving(a => ({ ...a, [id]: true }));
    const body = purchaseId ? { purchase_id: purchaseId } : {};
    axios.post(`${API}/admin/submissions/${id}/approve/`, body, authHeader)
      .then(res => {
        setMatchResults(r => ({ ...r, [id]: res.data }));
        if (["matched", "created"].includes(res.data.match_status)) loadSubmissions();
      })
      .catch(err => setMatchResults(r => ({ ...r, [id]: { match_status: "error", reason: JSON.stringify(err.response?.data) } })))
      .finally(() => setApproving(a => ({ ...a, [id]: false })));
  };

  const rejectSubmission = (id) => {
    axios.patch(`${API}/admin/submissions/${id}/status/`, { status: "rejected" }, authHeader)
      .then(() => { setViewingSubmission(null); loadSubmissions(); })
      .catch(console.error);
  };

  const resetSubmission = (id) => {
    axios.patch(`${API}/admin/submissions/${id}/status/`, { status: "pending" }, authHeader)
      .then(() => { setViewingSubmission(null); loadSubmissions(); })
      .catch(console.error);
  };

  const deleteSubmission = (id) => {
    if (!window.confirm("Delete this submission? This cannot be undone.")) return;
    axios.delete(`${API}/admin/submissions/${id}/`, authHeader)
      .then(() => { setViewingSubmission(null); loadSubmissions(); })
      .catch(console.error);
  };

  const handleApprove = (purchaseId = null, force = false) => {
    if (!viewingSubmission) return;
    const id = viewingSubmission.id;
    // Auto-save current edits first, then approve
    setEditSaving(true);
    const savePayload = {
      ...editForm,
      line_items: editLineItems.map(li => ({
        category: li.category || null,
        description: li.description,
        amount: parseFloat(li.amount) || 0,
        expense_class: li.expense_class || null,
      })),
    };
    axios.patch(`${API}/admin/submissions/${id}/`, savePayload, authHeader)
      .then(() => {
        setApproving(a => ({ ...a, [id]: true }));
        const body = purchaseId ? { purchase_id: purchaseId } : (force ? { force: true } : {});
        return axios.post(`${API}/admin/submissions/${id}/approve/`, body, authHeader);
      })
      .then(res => {
        setViewMatchResult(res.data);
        if (["matched", "created"].includes(res.data.match_status)) loadSubmissions();
      })
      .catch(err => setViewMatchResult({ match_status: "error", reason: JSON.stringify(err.response?.data) }))
      .finally(() => { setEditSaving(false); setApproving(a => ({ ...a, [id]: false })); });
  };

  const handleUndo = () => {
    if (!viewingSubmission) return;
    if (!window.confirm("Undo this match? This will revert the QBO purchase to MISSING RECEIPT and restore the submission to pending.")) return;
    axios.post(`${API}/admin/submissions/${viewingSubmission.id}/undo/`, {}, authHeader)
      .then(() => { setViewingSubmission(null); setViewMatchResult(null); loadSubmissions(); loadMatches(); })
      .catch(err => setEditMsg(JSON.stringify(err.response?.data) || "Undo failed."));
  };

  const handleCreateExpense = () => {
    if (!viewingSubmission) return;
    const id = viewingSubmission.id;
    setApproving(a => ({ ...a, [id]: true }));
    axios.post(`${API}/admin/submissions/${id}/create-expense/`, {}, authHeader)
      .then(res => {
        setViewMatchResult(res.data);
        if (["matched", "created"].includes(res.data.match_status)) {
          if (res.data.submission) setViewingSubmission(res.data.submission);
          loadSubmissions();
        }
      })
      .catch(err => setViewMatchResult({ match_status: "error", reason: JSON.stringify(err.response?.data) }))
      .finally(() => setApproving(a => ({ ...a, [id]: false })));
  };

  const openView = (submission) => {
    if (!refLoaded) {
      axios.get(`${API}/reference/`, authHeader).then(res => {
        setRefVendors(res.data.vendors);
        setAccounts(res.data.accounts);
        setRefCategories(res.data.categories);
        setRefClasses(res.data.classes);
        setRefLoaded(true);
      });
    }
    setViewMatchResult(null);
    setEditMsg("");
    // Show stale data immediately so modal opens without delay
    const populate = (sub) => {
      setEditForm({
        vendor: sub.vendor || "",
        account: sub.account || "",
        date: sub.date,
        payment_type: sub.payment_type,
        memo: sub.memo || "",
      });
      setEditLineItems(
        (sub.line_items || []).map(li => ({
          category: li.category || "",
          description: li.description,
          amount: li.amount,
          expense_class: li.expense_class || "",
        }))
      );
      setViewingSubmission(sub);
    };
    populate(submission);
    // Then refresh with latest data from server
    axios.get(`${API}/admin/submissions/${submission.id}/`, authHeader)
      .then(res => populate(res.data))
      .catch(() => {}); // already showing stale data, silently ignore
  };

  const updateEditLineItem = (index, field, value) => {
    setEditLineItems(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
  };
  const TAX_CAT_DEFAULTS = { '8620': 'BC Tax 1', '8621': 'BC Tax 2' };
  const handleEditCategoryChange = (index, catId) => {
    const updates = { category: catId };
    const cat = refCategories.find(c => String(c.id) === String(catId));
    if (cat && TAX_CAT_DEFAULTS[cat.acct_num]) updates.description = TAX_CAT_DEFAULTS[cat.acct_num];
    setEditLineItems(prev => prev.map((item, i) => i === index ? { ...item, ...updates } : item));
  };

  const saveEdit = () => {
    setEditSaving(true);
    axios.patch(`${API}/admin/submissions/${viewingSubmission.id}/`, {
      ...editForm,
      line_items: editLineItems.map(li => ({
        category: li.category || null,
        description: li.description,
        amount: parseFloat(li.amount) || 0,
        expense_class: li.expense_class || null,
      })),
    }, authHeader)
      .then(() => { setViewingSubmission(null); loadSubmissions(); })
      .catch(err => setEditMsg(JSON.stringify(err.response?.data) || "Save failed."))
      .finally(() => setEditSaving(false));
  };

  const handleBatchApprove = () => {
    const ids = Object.keys(selected).filter(id => selected[id]).map(Number);
    if (!ids.length) return;
    setBatchApproving(true);
    setBatchResults(null);
    axios.post(`${API}/admin/submissions/batch-approve/`, { submission_ids: ids }, authHeader)
      .then(res => { setBatchResults(res.data.results); loadSubmissions(); })
      .catch(err => setBatchResults([{ match_status: "error", reason: JSON.stringify(err.response?.data) }]))
      .finally(() => setBatchApproving(false));
  };

  const handleBatchDelete = () => {
    const ids = Object.keys(selected).filter(id => selected[id]).map(Number);
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} submission${ids.length > 1 ? 's' : ''}? This cannot be undone.`)) return;
    setBatchDeleting(true);
    setBatchDeleteError("");
    Promise.all(ids.map(id => axios.delete(`${API}/admin/submissions/${id}/`, authHeader)))
      .then(() => { setSelected({}); loadSubmissions(); })
      .catch(err => setBatchDeleteError(`Delete failed: ${err.response?.data?.error || err.message}`))
      .finally(() => setBatchDeleting(false));
  };

  const toggleSelect = (id) => setSelected(s => ({ ...s, [id]: !s[id] }));
  const selectAll = () => {
    const selectable = submissions.filter(s => s.status === "pending" || s.status === "needs_review");
    const allSelected = selectable.length > 0 && selectable.every(s => selected[s.id]);
    const next = {};
    selectable.forEach(s => { next[s.id] = !allSelected; });
    setSelected(next);
  };

  // ── User actions ────────────────────────────────────────────────────────────

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setUserMsg("");
    try {
      const { group_id, ...userData } = newUser;
      const res = await axios.post(`${API}/admin/users/`, userData, authHeader);
      if (group_id) {
        await axios.patch(`${API}/admin/users/${res.data.id}/`, { group_id: parseInt(group_id) }, authHeader);
      }
      setUserMsg("User created.");
      setNewUser({ username: "", email: "", first_name: "", last_name: "", password: "", role: "user", group_id: "" });
      loadUsers();
    } catch (err) {
      setUserMsg("Error: " + JSON.stringify(err.response?.data));
    }
  };

  const updateUserRole = (id, role) => {
    axios.patch(`${API}/admin/users/${id}/`, { role }, authHeader)
      .then(() => loadUsers()).catch(console.error);
  };

  const resetPassword = (id) => {
    const pw = prompt("Enter new password for this user:");
    if (!pw) return;
    axios.patch(`${API}/admin/users/${id}/`, { password: pw }, authHeader)
      .then(() => alert("Password updated.")).catch(console.error);
  };

  const deleteUser = (id, username) => {
    if (!window.confirm(`Delete user "${username}"?`)) return;
    axios.delete(`${API}/admin/users/${id}/`, authHeader)
      .then(() => loadUsers()).catch(console.error);
  };

  const saveUserEdit = () => {
    if (!editingUser) return;
    const { id, first_name, last_name, email } = editingUser;
    axios.patch(`${API}/admin/users/${id}/`, { first_name, last_name, email }, authHeader)
      .then(() => { loadUsers(); setEditingUser(null); })
      .catch(err => alert(err.response?.data ? JSON.stringify(err.response.data) : "Save failed."));
  };

  // ── Account actions ─────────────────────────────────────────────────────────

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setAccountMsg("");
    try {
      await axios.post(`${API}/admin/accounts/`, newAccount, authHeader);
      setAccountMsg("Account created.");
      setNewAccount({ name: "", qbo_id: "", account_type: "" });
      loadAccounts();
    } catch (err) {
      setAccountMsg("Error: " + JSON.stringify(err.response?.data));
    }
  };

  const deleteAccount = (id, name) => {
    if (!window.confirm(`Delete account "${name}"?`)) return;
    axios.delete(`${API}/admin/accounts/${id}/`, authHeader)
      .then(() => loadAccounts()).catch(console.error);
  };

  // ── Card actions ─────────────────────────────────────────────────────────────

  const loadCards = () => {
    setCardsLoading(true);
    axios.get(`${API}/admin/cards/`, authHeader)
      .then(res => setCards(res.data))
      .catch(console.error)
      .finally(() => setCardsLoading(false));
  };

  const handleCreateCard = async (e) => {
    e.preventDefault();
    setCardMsg("");
    const payload = { ...newCard };
    if (!payload.user) delete payload.user;
    try {
      await axios.post(`${API}/admin/cards/`, payload, authHeader);
      setCardMsg("Card added.");
      setNewCard({ last_four: "", account: "", user: "" });
      loadCards();
    } catch (err) {
      setCardMsg("Error: " + JSON.stringify(err.response?.data));
    }
  };

  const saveCard = async (id) => {
    if (!editingCard) return;
    const payload = { ...editingCard };
    if (!payload.user) delete payload.user;
    await axios.patch(`${API}/admin/cards/${id}/`, payload, authHeader).catch(console.error);
    setEditingCard(null);
    loadCards();
  };

  const deleteCard = (id, label) => {
    if (!window.confirm(`Delete card "${label}"?`)) return;
    axios.delete(`${API}/admin/cards/${id}/`, authHeader)
      .then(() => loadCards()).catch(console.error);
  };

  // ── QBO sync ────────────────────────────────────────────────────────────────

  const handleSyncQBO = (type) => {
    setSyncing(true);
    setSyncMsg("");
    axios.post(`${API}/admin/sync-qbo/`, { type }, authHeader)
      .then(res => setSyncMsg(JSON.stringify(res.data.results || res.data, null, 2)))
      .catch(err => setSyncMsg("Sync failed: " + JSON.stringify(err.response?.data)))
      .finally(() => setSyncing(false));
  };

  // ── Bank CSV ────────────────────────────────────────────────────────────────

  const handleCSVPreview = async (e) => {
    e.preventDefault();
    if (!csvFile) return;
    setCsvMsg("");
    setCsvPreviewRows(null);
    setCsvCounts(null);
    setCsvUploading(true);
    const form = new FormData();
    form.append("csv_file", csvFile);
    form.append("confirm", "false");
    if (csvForceCardId) form.append("force_card_id", csvForceCardId);
    if (csvImportAll) {
      form.append("import_all", "true");
      if (csvDefaultAccountId) form.append("default_account_id", csvDefaultAccountId);
    }
    try {
      const res = await axios.post(`${API}/admin/upload-csv/`, form, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setCsvPreviewRows(res.data.rows);
      setCsvCounts(res.data.counts);
    } catch (err) {
      setCsvMsg("Error: " + JSON.stringify(err.response?.data));
    } finally {
      setCsvUploading(false);
    }
  };

  const handleCSVConfirm = async () => {
    const toCreate = (csvPreviewRows || []).filter(r => r.status === "preview");
    if (!toCreate.length || !window.confirm(`Send ${toCreate.length} expenses to QuickBooks?`)) return;
    setCsvUploading(true);
    const form = new FormData();
    form.append("csv_file", csvFile);
    form.append("confirm", "true");
    form.append("rows", JSON.stringify(toCreate));
    try {
      const res = await axios.post(`${API}/admin/upload-csv/`, form, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setCsvPreviewRows(res.data.rows);
      setCsvCounts(res.data.counts);
      setCsvMsg(`Done — ${res.data.counts.created} created, ${res.data.counts.errors} errors.`);
    } catch (err) {
      setCsvMsg("Error: " + JSON.stringify(err.response?.data));
    } finally {
      setCsvUploading(false);
    }
  };

  // ── Invite ───────────────────────────────────────────────────────────────────

  const sendInvite = async e => {
    e.preventDefault();
    setInviteMsg("");
    setInviteSending(true);
    try {
      const res = await axios.post(`${AUTH_API}/invite/`, inviteForm, authHeader);
      setInviteMsg(res.data.message);
      setInviteForm({ email: "", username: "", role: "user" });
    } catch (err) {
      setInviteMsg("Error: " + (err.response?.data?.error || "Failed to send invite."));
    } finally {
      setInviteSending(false);
    }
  };

  const sendWelcomeEmail = async (userId) => {
    setWelcomeSending(s => ({ ...s, [userId]: true }));
    try {
      const res = await axios.post(`${AUTH_API}/users/${userId}/send-welcome/`, {}, authHeader);
      alert(res.data.message);
    } catch (err) {
      alert("Error: " + (err.response?.data?.error || "Failed to send email."));
    } finally {
      setWelcomeSending(s => ({ ...s, [userId]: false }));
    }
  };

  // ── Missing Receipt Report ────────────────────────────────────────────────────

  const generateReport = async (sendNow = false) => {
    setReportLoading(true);
    setReportMsg("");
    setReportDrafts(null);
    try {
      const res = await axios.post(`${API}/admin/missing-report/`, {
        month: reportMonth,
        custom_message: reportMessage,
        preview: !sendNow,
      }, authHeader);
      if (sendNow) {
        const { sent, skipped } = res.data;
        setReportMsg(`Sent ${sent.length} email(s).${skipped.length ? ` Skipped (no email): ${skipped.join(", ")}.` : ""}`);
      } else {
        setReportDrafts(res.data.emails || []);
        setReportNoEmail(res.data.no_email || []);
        if (!res.data.emails?.length) setReportMsg(res.data.message || "No missing receipts found.");
      }
    } catch (err) {
      setReportMsg("Error: " + (err.response?.data?.error || "Report generation failed."));
    } finally {
      setReportLoading(false);
    }
  };

  const sendSingleEmail = async (draft) => {
    try {
      await axios.post(`${API}/admin/missing-report/`, {
        month: reportMonth,
        custom_message: reportMessage,
        preview: false,
      }, authHeader);
    } catch (err) {
      alert("Send failed: " + (err.response?.data?.error || "Unknown error"));
    }
  };

  // ── Helpers ─────────────────────────────────────────────────────────────────

  const STATUS_COLORS = { pending: "#f0ad4e", needs_review: "#e67e22", submitted: "#5cb85c", posted: "#16a085", rejected: "#d9534f" };
  const STATUS_LABELS = { pending: "Pending", needs_review: "Needs Review", submitted: "Matched", posted: "Posted", rejected: "Rejected" };
  const pendingCount = submissions.filter(s => s.status === "pending").length;
  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <div className="admin-page">
      <h1>Admin Dashboard</h1>

      <div className="tab-bar">
        <button className="tab-btn" onClick={() => navigate("/submit")}>+ Submit Receipt</button>
        <button className="tab-btn" onClick={() => navigate("/missing-receipts")}>Missing Receipts</button>
        {["submissions", "submitted", "users", "groups", "accounts", "cards", "csv", "sync", "reports", "settings"].map(tab => (
          <button
            key={tab}
            className={`tab-btn ${activeTab === tab ? "active" : ""}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === "csv" ? "Upload Bank CSV" : tab === "submitted" ? "Submitted" : tab.charAt(0).toUpperCase() + tab.slice(1)}
            {tab === "submissions" && pendingCount > 0 && (
              <span className="badge">{pendingCount}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── Submissions tab ── */}
      {activeTab === "submissions" && (
        <div className="tab-content">
          <div className="tab-header">
            <h2>Receipt Submissions</h2>
            <div style={{ display: "flex", gap: 8 }}>
              {selectedCount > 0 && (
                <>
                  <button className="btn-primary" disabled={batchApproving} onClick={handleBatchApprove}>
                    {batchApproving ? "Approving..." : `Approve Selected (${selectedCount})`}
                  </button>
                  <button className="btn-danger" disabled={batchDeleting} onClick={handleBatchDelete}>
                    {batchDeleting ? "Deleting..." : `Delete Selected (${selectedCount})`}
                  </button>
                </>
              )}
              <button className="btn-secondary" onClick={loadSubmissions}>Refresh</button>
            </div>
          </div>

          {batchDeleteError && (
            <div className="card mb-2" style={{ color: '#c0392b', fontWeight: 500 }}>{batchDeleteError}</div>
          )}
          {batchResults && (
            <div className="card mb-2">
              <h4>Batch Approve Results</h4>
              {batchResults.map((r, i) => (
                <div key={i} className={`match-result ${r.match_status}`}>
                  <strong>#{r.id}</strong> — {r.match_status}
                  {r.reason && <span className="muted"> ({r.reason})</span>}
                  {r.qbo_purchase_id && <span className="muted"> QBO: {r.qbo_purchase_id}</span>}
                </div>
              ))}
            </div>
          )}

          {submissionsLoading && <p>Loading...</p>}
          {!submissionsLoading && submissions.length === 0 && <p className="empty">No submissions yet.</p>}
          {submissions.length > 0 && (
            <>
              {/* Mobile cards */}
              <div className="mobile-card-list">
                {submissions.map(s => (
                  <div key={s.id} className="mobile-card">
                    <div className="mobile-card-row">
                      <div>
                        <div className="mobile-card-vendor">{s.vendor_name || <span className="muted">Unknown vendor</span>}</div>
                        <div className="mobile-card-meta">{s.submitted_by_name} · {s.date}</div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div className="mobile-card-amount">${parseFloat(s.total_amount).toFixed(2)}</div>
                        <span className="status-badge" style={{ background: STATUS_COLORS[s.status] }}>
                          {STATUS_LABELS[s.status] || s.status}
                        </span>
                      </div>
                    </div>
                    <div className="mobile-card-actions">
                      {(s.status === "pending" || s.status === "needs_review") && (
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>
                          <input type="checkbox" checked={!!selected[s.id]} onChange={() => toggleSelect(s.id)} />
                          Select
                        </label>
                      )}
                      <button className="btn-action" onClick={() => openView(s)}>View</button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop table */}
            <table className="data-table desktop-only">
              <thead>
                <tr>
                  <th>
                    <input type="checkbox" onChange={selectAll}
                      checked={submissions.some(s => s.status === "pending" || s.status === "needs_review") && submissions.filter(s => s.status === "pending" || s.status === "needs_review").every(s => selected[s.id])} />
                  </th>
                  <th>Date</th>
                  <th>Submitted By</th>
                  <th>Vendor</th>
                  <th>Account</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {submissions.map(s => {
                  const result = matchResults[s.id];
                  return (
                    <>
                      <tr key={s.id}>
                        <td>
                          {(s.status === "pending" || s.status === "needs_review") && (
                            <input type="checkbox" checked={!!selected[s.id]} onChange={() => toggleSelect(s.id)} />
                          )}
                        </td>
                        <td>{s.date}</td>
                        <td>{s.submitted_by_name}</td>
                        <td>{s.vendor_name || <span className="muted">—</span>}</td>
                        <td>{displayAccountName(s.account_name) || <span className="muted">—</span>}</td>
                        <td>${parseFloat(s.total_amount).toFixed(2)}</td>
                        <td>
                          <span className="status-badge" style={{ background: STATUS_COLORS[s.status] }}>
                            {STATUS_LABELS[s.status] || s.status}
                          </span>
                          {s.qbo_purchase_id && <div className="muted" style={{ fontSize: 11 }}>QBO: {s.qbo_purchase_id}</div>}
                        </td>
                        <td className="action-cell">
                          <button className="btn-action" onClick={() => openView(s)}>View</button>
                        </td>
                      </tr>
                    </>
                  );
                })}
              </tbody>
            </table>
            </>
          )}
        </div>
      )}

      {/* ── Users tab ── */}
      {activeTab === "users" && (
        <div className="tab-content">
          <div className="tab-header">
            <h2>Users</h2>
            <button className="btn-primary" onClick={() => { setShowInviteModal(true); setInviteMsg(""); }}>
              Invite User
            </button>
          </div>
          {usersLoading && <p>Loading...</p>}
          {users.length > 0 && (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Username</th><th>Name</th><th>Email</th><th>Role</th><th>Group</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => {
                  const editing = editingUser?.id === u.id;
                  return (
                    <tr key={u.id}>
                      <td>{u.username}</td>
                      <td>
                        {editing ? (
                          <span style={{ display: "flex", gap: 4 }}>
                            <input value={editingUser.first_name} placeholder="First" style={{ width: 80 }}
                              onChange={e => setEditingUser(eu => ({ ...eu, first_name: e.target.value }))} />
                            <input value={editingUser.last_name} placeholder="Last" style={{ width: 80 }}
                              onChange={e => setEditingUser(eu => ({ ...eu, last_name: e.target.value }))} />
                          </span>
                        ) : `${u.first_name} ${u.last_name}`}
                      </td>
                      <td>
                        {editing ? (
                          <input type="email" value={editingUser.email} style={{ width: 180 }}
                            onChange={e => setEditingUser(eu => ({ ...eu, email: e.target.value }))} />
                        ) : u.email}
                      </td>
                      <td>
                        <select value={u.role} onChange={e => updateUserRole(u.id, e.target.value)}>
                          <option value="user">User</option>
                          <option value="receipt_entry">Receipt Entry</option>
                          <option value="admin">Admin</option>
                        </select>
                      </td>
                      <td>
                        <select
                          value={u.group_id || ""}
                          onChange={e => {
                            const gid = e.target.value ? parseInt(e.target.value) : null;
                            axios.patch(`${API}/admin/users/${u.id}/`, { group_id: gid }, authHeader)
                              .then(res => setUsers(us => us.map(x => x.id === u.id ? { ...x, group_id: res.data.group_id, group_name: res.data.group_name } : x)))
                              .catch(console.error);
                          }}
                          style={{ minWidth: 120 }}
                        >
                          <option value="">— No group —</option>
                          {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                        </select>
                      </td>
                      <td className="action-cell">
                        {editing ? (
                          <span style={{ display: "flex", gap: 6 }}>
                            <button className="btn-action approve" onClick={saveUserEdit}>Save</button>
                            <button className="btn-action" onClick={() => setEditingUser(null)}>Cancel</button>
                          </span>
                        ) : (
                          <div className="user-menu-wrap" ref={openMenuId === u.id ? menuRef : null}>
                            <button
                              className="btn-kebab"
                              title="Actions"
                              onClick={() => setOpenMenuId(openMenuId === u.id ? null : u.id)}
                            >⋯</button>
                            {openMenuId === u.id && (
                              <div className="user-menu-dropdown">
                                <button onClick={() => { setEditingUser({ id: u.id, first_name: u.first_name || "", last_name: u.last_name || "", email: u.email || "" }); setOpenMenuId(null); }}>Edit</button>
                                <button onClick={() => { resetPassword(u.id); setOpenMenuId(null); }}>Reset Password</button>
                                <button onClick={() => {
                                  if (!window.confirm(`Disable 2FA for ${u.username}?`)) return;
                                  axios.post(`${AUTH_API}/totp/disable/${u.id}/`, {}, authHeader)
                                    .then(() => alert("2FA disabled.")).catch(console.error);
                                  setOpenMenuId(null);
                                }}>Reset 2FA</button>
                                <button
                                  disabled={!u.email || welcomeSending[u.id]}
                                  title={u.email ? `Send login info to ${u.email}` : "No email on file"}
                                  onClick={() => { sendWelcomeEmail(u.id); setOpenMenuId(null); }}>
                                  {welcomeSending[u.id] ? "Sending…" : "Send Login Info"}
                                </button>
                                <button className="danger" onClick={() => { deleteUser(u.id, u.username); setOpenMenuId(null); }}>Delete</button>
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          <div className="card mt-2">
            <h3>Add New User</h3>
            {userMsg && <p className={userMsg.startsWith("Error") ? "msg-error" : "msg-ok"}>{userMsg}</p>}
            <form onSubmit={handleCreateUser} className="inline-form">
              <input placeholder="Username" required value={newUser.username} onChange={e => setNewUser(u => ({ ...u, username: e.target.value }))} />
              <input placeholder="First name" value={newUser.first_name} onChange={e => setNewUser(u => ({ ...u, first_name: e.target.value }))} />
              <input placeholder="Last name" value={newUser.last_name} onChange={e => setNewUser(u => ({ ...u, last_name: e.target.value }))} />
              <input type="email" placeholder="Email" required value={newUser.email} onChange={e => setNewUser(u => ({ ...u, email: e.target.value }))} />
              <input type="password" placeholder="Password" required value={newUser.password} onChange={e => setNewUser(u => ({ ...u, password: e.target.value }))} />
              <select value={newUser.role} onChange={e => setNewUser(u => ({ ...u, role: e.target.value }))}>
                <option value="user">User</option>
                <option value="receipt_entry">Receipt Entry</option>
                <option value="admin">Admin</option>
              </select>
              <select value={newUser.group_id} onChange={e => setNewUser(u => ({ ...u, group_id: e.target.value }))}>
                <option value="">— No group —</option>
                {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
              <button type="submit" className="btn-primary">Add User</button>
            </form>
          </div>
        </div>
      )}

      {/* ── Groups tab ── */}
      {activeTab === "groups" && (
        <div className="tab-content">
          <div className="tab-header"><h2>Groups &amp; Default Preferences</h2></div>
          <p className="muted" style={{ marginBottom: "1rem" }}>
            Each group sets default categories and classes for its members. Users can still narrow their preferences personally in Settings.
          </p>
          {groupMsg && <p className={groupMsg.includes("fail") || groupMsg.includes("Error") ? "msg-error" : "msg-ok"} style={{ marginBottom: "0.75rem" }}>{groupMsg}</p>}

          {/* Global Default card */}
          {globalPref && (
            <div className="card" style={{ marginBottom: "1.5rem" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: editingGlobal ? "0.75rem" : 0 }}>
                <div>
                  <h3 style={{ marginBottom: "0.15rem" }}>Global Default</h3>
                  {!editingGlobal && (
                    <p className="muted" style={{ fontSize: 13, margin: 0 }}>
                      {globalPref.category_ids.length === 0 ? "All categories" : `${globalPref.category_ids.length} categories`}
                      {" · "}
                      {globalPref.class_ids.length === 0 ? "All classes" : `${globalPref.class_ids.length} classes`}
                    </p>
                  )}
                </div>
                {!editingGlobal && (
                  <button className="btn-action approve" onClick={() => { setEditingGlobal(true); setGlobalPrefMsg(""); }}>Edit</button>
                )}
              </div>
              {globalPrefMsg && <p className={globalPrefMsg.includes("fail") ? "msg-error" : "msg-ok"} style={{ marginBottom: "0.5rem" }}>{globalPrefMsg}</p>}
              {editingGlobal && (
                <>
                  <p className="muted" style={{ marginBottom: "0.75rem" }}>
                    Applies to users who don't belong to any group. Empty = show all.
                  </p>
                  <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap" }}>
                    <div style={{ flex: 1, minWidth: 220 }}>
                      <h4 style={{ marginBottom: "0.4rem" }}>Categories
                        <button className="btn-link" style={{ marginLeft: 12, fontSize: 12 }}
                          onClick={() => setGlobalPref(gp => ({ ...gp, category_ids: allRefCategories.map(c => c.id) }))}>
                          Select All
                        </button>
                        <button className="btn-link" style={{ marginLeft: 8, fontSize: 12 }}
                          onClick={() => setGlobalPref(gp => ({ ...gp, category_ids: [] }))}>
                          Unselect All
                        </button>
                      </h4>
                      <SelectList
                        items={allRefCategories}
                        selectedIds={globalPref.category_ids}
                        onToggle={id => toggleGlobalItem(id, "category_ids")}
                        height="240px"
                      />
                    </div>
                    <div style={{ flex: 1, minWidth: 180 }}>
                      <h4 style={{ marginBottom: "0.4rem" }}>Ministries
                        <button className="btn-link" style={{ marginLeft: 12, fontSize: 12 }}
                          onClick={() => setGlobalPref(gp => ({ ...gp, class_ids: allRefClasses.map(c => c.id) }))}>
                          Select All
                        </button>
                        <button className="btn-link" style={{ marginLeft: 8, fontSize: 12 }}
                          onClick={() => setGlobalPref(gp => ({ ...gp, class_ids: [] }))}>
                          Unselect All
                        </button>
                      </h4>
                      <SelectList
                        items={allRefClasses}
                        selectedIds={globalPref.class_ids}
                        onToggle={id => toggleGlobalItem(id, "class_ids")}
                        height="240px"
                      />
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button className="btn-primary" onClick={saveGlobalPref} disabled={globalPrefSaving}>
                      {globalPrefSaving ? "Saving..." : "Save"}
                    </button>
                    <button className="btn-secondary" onClick={() => { setEditingGlobal(false); setGlobalPrefMsg(""); }}>Cancel</button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Group list */}
          {groupsLoading && <p>Loading...</p>}
          {groups.length > 0 && (
            <table className="data-table" style={{ marginBottom: "1.5rem" }}>
              <thead>
                <tr><th>Group</th><th>Members</th><th>Categories</th><th>Ministries</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {groups.map(g => (
                  <tr key={g.id} className={editingGroup?.id === g.id ? "editing-row" : ""}>
                    <td><strong>{g.name}</strong></td>
                    <td>{g.user_count}</td>
                    <td>{g.category_ids.length === 0 ? <span className="muted">All</span> : g.category_ids.length}</td>
                    <td>{g.class_ids.length === 0 ? <span className="muted">All</span> : g.class_ids.length}</td>
                    <td className="action-cell">
                      <button className="btn-action approve" onClick={() => { setEditingGroup({ ...g }); setGroupMsg(""); }}>Edit</button>
                      <button className="btn-action reject" onClick={() => deleteGroup(g.id, g.name)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Inline group preference editor */}
          {editingGroup && (
            <div className="card" style={{ marginBottom: "1.5rem" }}>
              <h3>Editing: {editingGroup.name}</h3>
              <p className="muted" style={{ marginBottom: "0.75rem" }}>
                Empty selection = members see <strong>all</strong> categories/classes (no filter applied).
              </p>

              <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <h4 style={{ marginBottom: "0.4rem" }}>Categories
                    <button className="btn-link" style={{ marginLeft: 12, fontSize: 12 }}
                      onClick={() => setEditingGroup(eg => ({ ...eg, category_ids: allRefCategories.map(c => c.id) }))}>
                      Select All
                    </button>
                    <button className="btn-link" style={{ marginLeft: 8, fontSize: 12 }}
                      onClick={() => setEditingGroup(eg => ({ ...eg, category_ids: [] }))}>
                      Unselect All
                    </button>
                  </h4>
                  <SelectList
                    items={allRefCategories}
                    selectedIds={editingGroup.category_ids}
                    onToggle={id => toggleGroupItem(id, "category_ids")}
                    height="240px"
                  />
                </div>
                <div style={{ flex: 1, minWidth: 180 }}>
                  <h4 style={{ marginBottom: "0.4rem" }}>Ministries
                    <button className="btn-link" style={{ marginLeft: 12, fontSize: 12 }}
                      onClick={() => setEditingGroup(eg => ({ ...eg, class_ids: allRefClasses.map(c => c.id) }))}>
                      Select All
                    </button>
                    <button className="btn-link" style={{ marginLeft: 8, fontSize: 12 }}
                      onClick={() => setEditingGroup(eg => ({ ...eg, class_ids: [] }))}>
                      Unselect All
                    </button>
                  </h4>
                  <SelectList
                    items={allRefClasses}
                    selectedIds={editingGroup.class_ids}
                    onToggle={id => toggleGroupItem(id, "class_ids")}
                    height="240px"
                  />
                </div>
              </div>

              <div style={{ display: "flex", gap: "0.5rem" }}>
                <button className="btn-primary" onClick={saveGroupPrefs}>Save</button>
                <button className="btn-secondary" onClick={() => { setEditingGroup(null); setGroupMsg(""); }}>Cancel</button>
              </div>
            </div>
          )}

          {/* Add new group */}
          <div className="card">
            <h3>Add Group</h3>
            <form onSubmit={createGroup} className="inline-form">
              <input
                placeholder="Group name (e.g. Worship)"
                value={newGroupName}
                onChange={e => setNewGroupName(e.target.value)}
                required
              />
              <button type="submit" className="btn-primary">Add Group</button>
            </form>
          </div>
        </div>
      )}

      {/* ── Accounts tab ── */}
      {activeTab === "accounts" && (
        <div className="tab-content">
          <div className="tab-header"><h2>Accounts</h2></div>
          {accountsLoading && <p>Loading...</p>}
          {accounts.length > 0 && (
            <table className="data-table">
              <thead>
                <tr><th>Name</th><th>QBO ID</th><th>Type</th><th>Active</th><th>Default Owner</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {accounts.map(a => (
                  <tr key={a.id}>
                    <td>{a.name}</td>
                    <td>{a.qbo_id || <span className="muted">—</span>}</td>
                    <td>{a.account_type || <span className="muted">—</span>}</td>
                    <td>{a.is_active ? "Yes" : "No"}</td>
                    <td>
                      <select
                        value={a.default_owner || ""}
                        title="Missing Receipts ownership fallback when the bank description has no card last-4"
                        onChange={e => {
                          const val = e.target.value ? parseInt(e.target.value) : null;
                          axios.patch(`${API}/admin/accounts/${a.id}/`, { default_owner: val }, authHeader)
                            .then(res => setAccounts(prev => prev.map(x => x.id === a.id ? res.data : x)))
                            .catch(console.error);
                        }}
                        style={{ minWidth: 140 }}
                      >
                        <option value="">— None —</option>
                        {users.map(u => (
                          <option key={u.id} value={u.id}>{`${u.first_name} ${u.last_name}`.trim() || u.username}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <button className="btn-action reject" onClick={() => deleteAccount(a.id, a.name)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="card mt-2">
            <h3>Add Account</h3>
            {accountMsg && <p className={accountMsg.startsWith("Error") ? "msg-error" : "msg-ok"}>{accountMsg}</p>}
            <form onSubmit={handleCreateAccount} className="inline-form">
              <input placeholder="Account name" required value={newAccount.name} onChange={e => setNewAccount(a => ({ ...a, name: e.target.value }))} />
              <input placeholder="QBO account ID" value={newAccount.qbo_id} onChange={e => setNewAccount(a => ({ ...a, qbo_id: e.target.value }))} />
              <input placeholder="Type (Bank, Credit Card)" value={newAccount.account_type} onChange={e => setNewAccount(a => ({ ...a, account_type: e.target.value }))} />
              <button type="submit" className="btn-primary">Add Account</button>
            </form>
          </div>
        </div>
      )}

      {/* ── Cards tab ── */}
      {activeTab === "cards" && (
        <div className="tab-content">
          <div className="tab-header"><h2>Cards</h2></div>
          <p className="muted" style={{ marginBottom: 8 }}>
            Each card has a last-4, a cardholder name, and belongs to one account.
            Linking a card to a User is optional — it enables future features like auto-assignment.
          </p>
          {cardsLoading && <p>Loading...</p>}
          {cards.length > 0 && (
            <table className="data-table">
              <thead>
                <tr><th>Last 4</th><th>Account</th><th>User</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {cards.map(c => {
                  const editing = editingCard?.id === c.id;
                  return (
                    <tr key={c.id}>
                      <td>
                        {editing
                          ? <input value={editingCard.last_four} maxLength={4} style={{ width: 60 }}
                              onChange={e => setEditingCard(ec => ({ ...ec, last_four: e.target.value }))} />
                          : <code>...{c.last_four}</code>}
                      </td>
                      <td>
                        {editing
                          ? <select value={editingCard.account} style={{ width: 200 }}
                              onChange={e => setEditingCard(ec => ({ ...ec, account: e.target.value }))}>
                              <option value="">— select —</option>
                              {accounts.map(a => <option key={a.id} value={a.id}>{displayAccountName(a.name)}</option>)}
                            </select>
                          : displayAccountName(c.account_name)}
                      </td>
                      <td>
                        {editing
                          ? <select value={editingCard.user || ""} style={{ width: 140 }}
                              onChange={e => setEditingCard(ec => ({ ...ec, user: e.target.value || null }))}>
                              <option value="">— none —</option>
                              {users.map(u => <option key={u.id} value={u.id}>{u.username}</option>)}
                            </select>
                          : c.user_full_name || c.username || <span className="muted">Unassigned</span>}
                      </td>
                      <td className="action-cell">
                        {editing ? (
                          <>
                            <button className="btn-action approve" onClick={() => saveCard(c.id)}>Save</button>
                            <button className="btn-action" onClick={() => setEditingCard(null)}>Cancel</button>
                          </>
                        ) : (
                          <>
                            <button className="btn-action" onClick={() => setEditingCard({ id: c.id, last_four: c.last_four, account: c.account, user: c.user || "" })}>Edit</button>
                            <button className="btn-action reject" onClick={() => deleteCard(c.id, `...${c.last_four}`)}>Delete</button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          <div className="card mt-2">
            <h3>Add Card</h3>
            {cardMsg && <p className={cardMsg.startsWith("Error") ? "msg-error" : "msg-ok"}>{cardMsg}</p>}
            <form onSubmit={handleCreateCard} className="inline-form">
              <input placeholder="Last 4 digits" maxLength={4} required style={{ width: 80 }}
                value={newCard.last_four} onChange={e => setNewCard(c => ({ ...c, last_four: e.target.value }))} />
              <select required value={newCard.account} onChange={e => setNewCard(c => ({ ...c, account: e.target.value }))}>
                <option value="">— select account —</option>
                {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
              <select value={newCard.user} onChange={e => setNewCard(c => ({ ...c, user: e.target.value }))}>
                <option value="">— user (optional) —</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.username} ({u.first_name} {u.last_name})</option>)}
              </select>
              <button type="submit" className="btn-primary">Add Card</button>
            </form>
          </div>
        </div>
      )}

      {/* ── Bank CSV tab ── */}
      {activeTab === "csv" && (
        <div className="tab-content">
          <h2>Bank CSV Upload</h2>

          <div className="card">
            <form onSubmit={handleCSVPreview}>
              <div style={{ marginBottom: 12 }}>
                <input type="file" accept=".csv" onChange={e => {
                  setCsvFile(e.target.files[0]);
                  setCsvPreviewRows(null); setCsvCounts(null); setCsvMsg("");
                  setCsvForceCardId(""); setCsvImportAll(false); setCsvDefaultAccountId("");
                }} />
              </div>

              {/* Credit card mode — force all rows to one card */}
              {!csvImportAll && (
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>Credit card CSV — assign all rows to:</label>
                  <select value={csvForceCardId} onChange={e => { setCsvForceCardId(e.target.value); setCsvPreviewRows(null); setCsvCounts(null); }} style={{ minWidth: 260 }}>
                    <option value="">Auto-detect (debit / CKCD)</option>
                    {cards.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.user_full_name || c.username || `Card ...${c.last_four}`} — ···· {c.last_four} ({c.account_name})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Import all mode — include non-CKCD rows */}
              {!csvForceCardId && (
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 500, cursor: "pointer" }}>
                    <input type="checkbox" checked={csvImportAll} onChange={e => { setCsvImportAll(e.target.checked); setCsvPreviewRows(null); setCsvCounts(null); if (!e.target.checked) setCsvDefaultAccountId(""); }} />
                    Import all rows (operating account — includes non-card transactions)
                  </label>
                  {csvImportAll && (
                    <div style={{ marginTop: 8, marginLeft: 24 }}>
                      <label style={{ display: "block", marginBottom: 4 }}>Default account for unmatched rows:</label>
                      <select value={csvDefaultAccountId} onChange={e => { setCsvDefaultAccountId(e.target.value); setCsvPreviewRows(null); setCsvCounts(null); }} style={{ minWidth: 260 }}>
                        <option value="">— select account —</option>
                        {accounts.map(a => (
                          <option key={a.id} value={a.id}>{a.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}

              <button type="submit" className="btn-primary" disabled={!csvFile || csvUploading || (csvImportAll && !csvDefaultAccountId)}>
                {csvUploading ? "Processing..." : "Preview"}
              </button>
            </form>

            {csvMsg && <p className={csvMsg.startsWith("Error") ? "msg-error" : "msg-ok"} style={{ marginTop: 8 }}>{csvMsg}</p>}

            {csvPreviewRows && (
              <div style={{ marginTop: 16 }}>
                {csvCounts && (
                  <p>
                    <strong>{(csvPreviewRows || []).filter(r => r.status === "preview" || r.status === "created").length}</strong> would be created &nbsp;|&nbsp;
                    <strong>{csvCounts.skipped}</strong> skipped &nbsp;|&nbsp;
                    <strong>{csvCounts.errors}</strong> errors
                  </p>
                )}

                {csvPreviewRows.length > 0 && (
                  <table className="data-table" style={{ marginBottom: 12 }}>
                    <thead>
                      <tr><th>Status</th><th>Date</th><th>Vendor</th><th>Amount</th><th>Account</th><th>Cardholder</th><th>Detail</th><th></th></tr>
                    </thead>
                    <tbody>
                      {csvPreviewRows.map((r, i) => (
                        <tr key={i} style={{ opacity: r.status === "skipped" ? 0.5 : 1 }}>
                          <td><span className="status-badge" style={{ background: r.status === "preview" ? "#5cb85c" : r.status === "created" ? "#5cb85c" : r.status === "error" ? "#d9534f" : "#aaa" }}>{r.status}</span></td>
                          <td>{r.date || "—"}</td>
                          <td>{r.vendor || "—"}</td>
                          <td>{r.amount != null ? `$${parseFloat(r.amount).toFixed(2)}` : "—"}</td>
                          <td>{r.account || "—"}</td>
                          <td>
                            {r.status === "preview" ? (
                              <select
                                value={r.cardholder || ""}
                                onChange={e => {
                                  const updated = csvPreviewRows.map((row, idx) =>
                                    idx === i ? { ...row, cardholder: e.target.value } : row
                                  );
                                  setCsvPreviewRows(updated);
                                }}
                                style={{ minWidth: 140 }}
                              >
                                <option value="">(none)</option>
                                {users.map(u => (
                                  <option key={u.id} value={`${u.first_name} ${u.last_name}`.trim() || u.username}>
                                    {`${u.first_name} ${u.last_name}`.trim() || u.username}
                                  </option>
                                ))}
                              </select>
                            ) : (r.cardholder || "—")}
                          </td>
                          <td className="muted">{r.reason || r.qbo_id || ""}</td>
                          <td>
                            {r.status === "preview" && (
                              <button
                                className="btn-danger-sm"
                                title="Remove this row"
                                onClick={() => setCsvPreviewRows(csvPreviewRows.filter((_, idx) => idx !== i))}
                              >✕</button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {(csvPreviewRows || []).some(r => r.status === "preview") && (
                  <button className="btn-primary" disabled={csvUploading} onClick={handleCSVConfirm}>
                    {csvUploading ? "Sending..." : `Send ${(csvPreviewRows || []).filter(r => r.status === "preview").length} Expenses to QuickBooks`}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Submitted tab ── */}
      {activeTab === "submitted" && (
        <div className="tab-content">
          <div className="tab-header"><h2>Submitted Receipts</h2></div>
          {matchesLoading && <p>Loading...</p>}
          {!matchesLoading && matches.length === 0 && <p className="muted">No matched submissions yet.</p>}
          {matches.length > 0 && (
            <>
              {/* Mobile cards */}
              <div className="mobile-card-list">
                {matches.map(m => {
                  const hasMismatch = m.qbo_vendor_name !== m.sub_vendor_name || m.qbo_account_name !== m.sub_account_name;
                  return (
                    <div key={m.id} className={`mobile-card${hasMismatch ? ' mobile-card-mismatch' : ''}`}>
                      <div className="mobile-card-row">
                        <div>
                          <div className="mobile-card-vendor">{m.sub_vendor_name || <span className="muted">Unknown vendor</span>}</div>
                          <div className="mobile-card-meta">{m.sub_payment_date} · ${parseFloat(m.sub_total_amount || 0).toFixed(2)}</div>
                          {m.qbo_purchase_id && (
                            <a href={`https://qbo.intuit.com/app/expense?txnId=${m.qbo_purchase_id}`}
                              target="_blank" rel="noreferrer" className="mobile-card-qbo-link">
                              QBO #{m.qbo_purchase_id}
                            </a>
                          )}
                          {hasMismatch && <div className="mobile-card-mismatch-label">Mismatch — review details</div>}
                        </div>
                        <button className="btn-action" style={{ flexShrink: 0 }} onClick={() => {
                          const sub = submissions.find(s => s.id === m.submission_id);
                          if (sub) openView(sub);
                        }}>View</button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Desktop table */}
              <table className="data-table desktop-only">
                <thead>
                  <tr>
                    <th>Sub #</th>
                    <th>QBO Purchase</th>
                    <th>Matched</th>
                    <th colSpan={2}>Vendor</th>
                    <th colSpan={2}>Account</th>
                    <th colSpan={2}>Date</th>
                    <th colSpan={2}>Amount</th>
                    <th></th>
                  </tr>
                  <tr style={{ fontSize: "0.8rem", color: "#666" }}>
                    <th></th><th></th><th></th>
                    <th>QBO</th><th>Submitted</th>
                    <th>QBO</th><th>Submitted</th>
                    <th>QBO</th><th>Submitted</th>
                    <th>QBO</th><th>Submitted</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {matches.map(m => (
                    <tr key={m.id} className={
                      m.qbo_vendor_name !== m.sub_vendor_name || m.qbo_account_name !== m.sub_account_name
                        ? "mismatch-row" : ""
                    }>
                      <td>#{m.submission_id}</td>
                      <td>
                        {m.qbo_purchase_id
                          ? <a href={`https://qbo.intuit.com/app/expense?txnId=${m.qbo_purchase_id}`}
                              target="_blank" rel="noreferrer" style={{ fontSize: "0.85rem" }}>
                              {m.qbo_purchase_id}
                            </a>
                          : <span className="muted">—</span>}
                      </td>
                      <td style={{ fontSize: "0.8rem" }}>{new Date(m.matched_at).toLocaleDateString()}</td>
                      <td>{m.qbo_vendor_name || <span className="muted">—</span>}</td>
                      <td>{m.sub_vendor_name || <span className="muted">—</span>}</td>
                      <td>{m.qbo_account_name || <span className="muted">—</span>}</td>
                      <td>{m.sub_account_name || <span className="muted">—</span>}</td>
                      <td>{m.qbo_payment_date || <span className="muted">—</span>}</td>
                      <td>{m.sub_payment_date || <span className="muted">—</span>}</td>
                      <td>${parseFloat(m.qbo_total_amount || 0).toFixed(2)}</td>
                      <td>${parseFloat(m.sub_total_amount || 0).toFixed(2)}</td>
                      <td>
                        <button className="btn-action" onClick={() => {
                          const sub = submissions.find(s => s.id === m.submission_id);
                          if (sub) openView(sub);
                        }}>View</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}

      {/* ── QBO Sync tab ── */}
      {activeTab === "sync" && (
        <div className="tab-content">
          <h2>QuickBooks Sync</h2>

          {/* Connection status */}
          <div className={`qbo-status ${qboConnected ? "connected" : "disconnected"}`}>
            {qboConnected === null && <span>Checking connection...</span>}
            {qboConnected === true && (
              <span>
                ✓ Connected to QuickBooks &nbsp;
                {connectUrl
                  ? <a href={connectUrl} target="_blank" rel="noreferrer" className="btn-secondary btn-sm">Re-connect</a>
                  : <span className="muted" style={{ fontSize: "0.85rem" }}> · Add credentials to enable re-connect</span>}
              </span>
            )}
            {qboConnected === false && (
              <span>
                ✗ Not connected. &nbsp;
                {credStatus?.has_credentials
                  ? <a href={connectUrl} target="_blank" rel="noreferrer" className="btn-primary btn-sm">Connect to QuickBooks</a>
                  : <span className="muted" style={{ fontSize: "0.85rem" }}>Add credentials below before connecting.</span>}
              </span>
            )}
            &nbsp;&nbsp;
            <button className="btn-secondary btn-sm" onClick={() => { setCredForm({ client_id: "", client_secret: "", redirect_uri: credStatus?.redirect_uri || "" }); setCredMsg(""); setShowCredModal(true); }}>
              {credStatus?.client_id_set ? "Update Credentials" : "Add Credentials"}
            </button>
          </div>

          {credStatus && (
            <p className="muted" style={{ fontSize: "0.85rem", marginTop: "0.5rem" }}>
              Credentials: Client ID {credStatus.client_id_set ? "✓ set" : "✗ not set"} · Client Secret {credStatus.client_secret_set ? "✓ set" : "✗ not set"} · Redirect URI: {credStatus.redirect_uri || "not set"}
            </p>
          )}

          {syncMsg && <pre className="alert-info" style={{ whiteSpace: "pre-wrap", fontSize: 13 }}>{syncMsg}</pre>}

          <div className="sync-buttons">
            {[
              { type: "vendors", label: "Vendors", desc: "Pull the vendor list from QBO into the dropdown." },
              { type: "accounts", label: "Accounts", desc: "Pull bank and credit card payment accounts from QBO." },
              { type: "categories", label: "Categories", desc: "Pull expense categories (chart of accounts) from QBO." },
              { type: "classes", label: "Classes", desc: "Pull QBO class list (e.g. 00 General Administration)." },
              { type: "all", label: "Sync All", desc: "Pull everything from QBO in one go." },
            ].map(({ type, label, desc }) => (
              <div className="sync-card" key={type}>
                <h3>{label}</h3>
                <p>{desc}</p>
                <button className="btn-primary" disabled={syncing || !qboConnected} onClick={() => handleSyncQBO(type)}>
                  {syncing ? "Syncing..." : `Sync ${label}`}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Settings tab ── */}
      {activeTab === "settings" && (
        <div className="tab-content">
          <h2>Settings</h2>

          {/* ── Receipt Form Preferences ── */}
          <div className="card mb-2">
            <h3 style={{ marginTop: 0 }}>Receipt Form Preferences</h3>
            <p className="muted" style={{ fontSize: "0.875rem", marginBottom: "0.75rem" }}>
              Choose which categories and classes appear in your receipt form when you submit as admin.
            </p>
            {prefMsg && <p className={prefMsg.includes("saved") ? "msg-ok" : "msg-error"} style={{ marginBottom: "0.5rem" }}>{prefMsg}</p>}
            {prefAllCategories.length > 0 && (
              <>
                <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap", marginBottom: "0.75rem" }}>
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <h4 style={{ marginBottom: "0.4rem" }}>Categories
                      <button className="btn-link" style={{ marginLeft: 12, fontSize: 12 }} onClick={() => setPrefCategoryIds(null)}>Select All</button>
                      <button className="btn-link" style={{ marginLeft: 8, fontSize: 12 }} onClick={() => setPrefCategoryIds([])}>Unselect All</button>
                    </h4>
                    <SelectList
                      items={prefAllCategories}
                      selectedIds={prefCategoryIds}
                      onToggle={id => togglePrefItem(id, prefCategoryIds, setPrefCategoryIds, prefAllCategories)}
                      height="220px"
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <h4 style={{ marginBottom: "0.4rem" }}>Ministries
                      <button className="btn-link" style={{ marginLeft: 12, fontSize: 12 }} onClick={() => setPrefClassIds(null)}>Select All</button>
                      <button className="btn-link" style={{ marginLeft: 8, fontSize: 12 }} onClick={() => setPrefClassIds([])}>Unselect All</button>
                    </h4>
                    <SelectList
                      items={prefAllClasses}
                      selectedIds={prefClassIds}
                      onToggle={id => togglePrefItem(id, prefClassIds, setPrefClassIds, prefAllClasses)}
                      height="220px"
                    />
                  </div>
                </div>
                <button className="btn-primary" onClick={saveAdminPreferences} disabled={prefSaving}>
                  {prefSaving ? "Saving..." : "Save"}
                </button>
              </>
            )}
          </div>

          {/* ── Purchase Filters ── */}
          <div className="card mb-2">
            <h3 style={{ marginTop: 0 }}>Purchase Filters</h3>
            <p className="muted" style={{ fontSize: "0.875rem", marginBottom: "0.75rem" }}>
              These filters define which QBO purchases are searched when matching a receipt.
              All active filters are combined with AND. The date window (below) is always applied in addition.
              <br />
              <strong>Note:</strong> Line item categories are not filterable via the QBO API — only the fields listed are supported.
            </p>
            {filtersLoading && <p>Loading...</p>}
            {filterMsg && <p className={filterMsg.startsWith("Save failed") ? "msg-error" : "msg-ok"}>{filterMsg}</p>}

            {filters.map((f, i) => {
              const isCategory = f.field_name === 'Category';
              const ops = isCategory ? ['=', '!='] : ['=', '!=', 'LIKE', 'NOT LIKE'];
              const opLabels = { '=': 'equals', '!=': 'not equals', 'LIKE': 'contains', 'NOT LIKE': 'does not contain' };

              return (
                <div key={i} style={{ display: "flex", gap: "0.5rem", alignItems: "center", marginBottom: "0.5rem", flexWrap: "wrap" }}>
                  <select value={f.field_name} onChange={e => updateLocalFilter(i, 'field_name', e.target.value)} style={{ minWidth: 160 }}>
                    <option value="DocNumber">Reference No.</option>
                    <option value="Category">Category (line item)</option>
                  </select>

                  <select value={f.operator} onChange={e => updateLocalFilter(i, 'operator', e.target.value)} style={{ minWidth: 130 }}>
                    {ops.map(op => <option key={op} value={op}>{opLabels[op]}</option>)}
                  </select>

                  {isCategory ? (
                    <select value={f.value} onChange={e => updateLocalFilter(i, 'value', e.target.value)} style={{ minWidth: 200 }}>
                      <option value="">-- select category --</option>
                      {filterCategories.map(c => <option key={c.id} value={c.qbo_id}>{displayName(c)}</option>)}
                    </select>
                  ) : (
                    <input type="text" value={f.value} onChange={e => updateLocalFilter(i, 'value', e.target.value)} placeholder="value" style={{ minWidth: 180 }} />
                  )}

                  <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.875rem" }}>
                    <input type="checkbox" checked={f.is_active} onChange={e => updateLocalFilter(i, 'is_active', e.target.checked)} />
                    Active
                  </label>

                  <button className="btn-action approve" onClick={() => saveFilter(i)}>Save</button>
                  <button className="btn-action reject" onClick={() => deleteFilter(i)}>Remove</button>
                </div>
              );
            })}

            <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem" }}>
              <button className="btn-secondary" onClick={addFilter}>+ Add Filter</button>
              <button className="btn-secondary" onClick={resetFilters}>Reset to Default</button>
            </div>
          </div>

          {/* ── Match Settings ── */}
          <div className="card mb-2">
            <h3 style={{ marginTop: 0 }}>Match Settings</h3>
            <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap", alignItems: "flex-end" }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label>Purchase Pool Window (days back)</label>
                <input type="number" min={1} max={365} value={matchSettings.pool_window_days}
                  onChange={e => setMatchSettings(s => ({ ...s, pool_window_days: e.target.value }))}
                  style={{ width: 80 }} />
                <small className="muted" style={{ display: "block" }}>Only pull QBO purchases from the last N days. Default: 45</small>
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label>Match Window (days ±)</label>
                <input type="number" min={0} max={30} value={matchSettings.date_window_days}
                  onChange={e => setMatchSettings(s => ({ ...s, date_window_days: e.target.value }))}
                  style={{ width: 80 }} />
                <small className="muted" style={{ display: "block" }}>Search ±N days from receipt date. Default: 3</small>
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label>Amount Tolerance ($)</label>
                <input type="number" min={0} step={0.01} value={matchSettings.amount_tolerance}
                  onChange={e => setMatchSettings(s => ({ ...s, amount_tolerance: e.target.value }))}
                  style={{ width: 90 }} />
                <small className="muted" style={{ display: "block" }}>Max dollar difference allowed. Default: 0.01</small>
              </div>
              <button className="btn-primary" disabled={matchSettingsSaving} onClick={saveMatchSettings}>
                {matchSettingsSaving ? "Saving..." : "Save"}
              </button>
            </div>
            {matchSettingsMsg && <p className={matchSettingsMsg.startsWith("Save failed") ? "msg-error" : "msg-ok"} style={{ marginTop: "0.5rem" }}>{matchSettingsMsg}</p>}
          </div>

          {/* ── Two-Factor Authentication ── */}
          <div className="card">
            <h3 style={{ marginTop: 0 }}>Two-Factor Authentication (TOTP)</h3>
            <p className="muted" style={{ fontSize: "0.875rem" }}>
              Adds a second login step using an authenticator app (Google Authenticator, Microsoft Authenticator, Authy).
              Scan the QR code once, then enter the 6-digit code on every login. Free — no SMS required.
            </p>
            {!totpStatus && (
              <button className="btn-secondary" onClick={loadTotpSetup}>Set Up 2FA for My Account</button>
            )}
            {totpStatus && (
              <div>
                {totpStatus.totp_enabled ? (
                  <p className="msg-ok">✓ Two-factor authentication is enabled for your account.</p>
                ) : (
                  <>
                    <p>Scan this QR code with your authenticator app, then enter the code below to confirm:</p>
                    <img src={totpStatus.qr_code} alt="TOTP QR code" style={{ width: 200, height: 200, border: "1px solid #dee2e6", borderRadius: 6, display: "block", marginBottom: "0.75rem" }} />
                    <p className="muted" style={{ fontSize: "0.8rem" }}>Manual entry key: <code>{totpStatus.secret}</code></p>
                  </>
                )}
                <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", marginTop: "0.75rem" }}>
                  <input type="text" inputMode="numeric" maxLength={6} placeholder="6-digit code"
                    value={totpCode} onChange={e => setTotpCode(e.target.value)} style={{ width: 130 }} />
                  <button className="btn-primary" onClick={confirmTotpSetup}>
                    {totpStatus.totp_enabled ? "Verify / Re-sync" : "Enable 2FA"}
                  </button>
                </div>
                {totpMsg && <p className={totpMsg.startsWith("Invalid") ? "msg-error" : "msg-ok"} style={{ marginTop: "0.5rem" }}>{totpMsg}</p>}
              </div>
            )}
            <p className="muted" style={{ fontSize: "0.8rem", marginTop: "1rem" }}>
              To disable or reset 2FA for another user, go to the Users tab → find the user → Reset 2FA.
            </p>
          </div>
        </div>
      )}

      {/* ── Reports tab ── */}
      {activeTab === "reports" && (
        <div className="tab-content">
          <div className="tab-header"><h2>Missing Receipt Reports</h2></div>
          <div className="card mb-2">
            <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", alignItems: "flex-end", marginBottom: "1rem" }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label>Month</label>
                <input type="month" value={reportMonth} onChange={e => setReportMonth(e.target.value)} />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", paddingBottom: "0.15rem" }}>
                <label style={{ margin: 0, cursor: "pointer", display: "flex", alignItems: "center", gap: "0.4rem" }}>
                  <input
                    type="checkbox" checked={reportPreview}
                    onChange={e => setReportPreview(e.target.checked)}
                  />
                  Preview before sending
                </label>
              </div>
            </div>
            <div className="form-group">
              <label>Custom intro message</label>
              <textarea
                rows={3}
                style={{ width: "100%", resize: "vertical" }}
                value={reportMessage}
                onChange={e => setReportMessage(e.target.value)}
              />
            </div>
            <div style={{ display: "flex", gap: "0.75rem", marginTop: "0.5rem" }}>
              <button className="btn-primary" disabled={reportLoading} onClick={() => generateReport(false)}>
                {reportLoading ? "Loading..." : reportPreview ? "Preview Emails" : "Generate"}
              </button>
              {!reportPreview && (
                <button className="btn-secondary" disabled={reportLoading} onClick={() => generateReport(true)}>
                  {reportLoading ? "Sending..." : "Send All"}
                </button>
              )}
            </div>
            {reportMsg && <p className={reportMsg.startsWith("Error") ? "msg-error" : "msg-ok"} style={{ marginTop: "0.75rem" }}>{reportMsg}</p>}
          </div>

          {reportNoEmail.length > 0 && (
            <div className="card mb-2" style={{ borderLeft: "3px solid #f0ad4e", background: "#fffbf0" }}>
              <strong>No email on file for:</strong> {reportNoEmail.join(", ")}
              <p className="muted" style={{ margin: "0.25rem 0 0", fontSize: "0.85rem" }}>
                Link a user account to their card so their email can be looked up.
              </p>
            </div>
          )}

          {reportDrafts && reportDrafts.length > 0 && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
                <h3 style={{ margin: 0 }}>{reportDrafts.length} email draft(s) for {reportMonth}</h3>
                <button className="btn-primary" disabled={reportLoading} onClick={() => generateReport(true)}>
                  Send All
                </button>
              </div>
              {reportDrafts.map((draft, i) => (
                <div key={i} className="card mb-1" style={{ borderLeft: draft.email ? "3px solid #5cb85c" : "3px solid #f0ad4e" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <strong>{draft.name}</strong>
                      {draft.email
                        ? <span className="muted" style={{ marginLeft: "0.5rem" }}>{draft.email}</span>
                        : <span style={{ marginLeft: "0.5rem", color: "#f0ad4e", fontSize: "0.85rem" }}>⚠ No email</span>
                      }
                      <span className="muted" style={{ marginLeft: "0.75rem", fontSize: "0.85rem" }}>
                        {draft.count} receipt(s) · ${parseFloat(draft.total).toFixed(2)}
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                      <button className="btn-secondary btn-sm" onClick={() => setExpandedEmail(expandedEmail === i ? null : i)}>
                        {expandedEmail === i ? "Hide" : "Preview"}
                      </button>
                    </div>
                  </div>
                  {expandedEmail === i && (
                    <pre style={{ marginTop: "0.75rem", background: "#f8f9fa", padding: "1rem", borderRadius: 4, fontSize: "0.85rem", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                      {draft.body}
                    </pre>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Invite modal ── */}
      {showInviteModal && (
        <div className="modal-overlay" onClick={() => setShowInviteModal(false)}>
          <div className="modal-panel" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <h2>Invite User</h2>
            <p className="muted" style={{ fontSize: "0.9rem", marginBottom: "1rem" }}>
              The recipient will receive an email with a one-time link to set up their account. The link expires in 48 hours.
            </p>
            {inviteMsg && <p className={inviteMsg.startsWith("Error") ? "msg-error" : "msg-ok"}>{inviteMsg}</p>}
            <form onSubmit={sendInvite}>
              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email" required
                  placeholder="staff@generationchurch.com"
                  value={inviteForm.email}
                  onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Username</label>
                <input
                  type="text" required
                  placeholder="e.g. jsmith"
                  value={inviteForm.username}
                  onChange={e => setInviteForm(f => ({ ...f, username: e.target.value.toLowerCase().replace(/\s/g, '') }))}
                />
                <p className="muted" style={{ fontSize: "0.8rem", marginTop: "0.25rem" }}>
                  This is what they'll type to log in. Lowercase, no spaces.
                </p>
              </div>
              <div className="form-group">
                <label>Role</label>
                <select value={inviteForm.role} onChange={e => setInviteForm(f => ({ ...f, role: e.target.value }))}>
                  <option value="user">User (assigned cards only)</option>
                  <option value="receipt_entry">Receipt Entry (all cards)</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn-primary" disabled={inviteSending}>
                  {inviteSending ? "Sending..." : "Send Invite"}
                </button>
                <button type="button" className="btn-secondary" onClick={() => setShowInviteModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Credentials modal ── */}
      {showCredModal && (
        <div className="modal-overlay" onClick={() => setShowCredModal(false)}>
          <div className="modal-panel" onClick={e => e.stopPropagation()} style={{ maxWidth: 540 }}>
            <h2>QuickBooks Credentials</h2>
            <p className="muted" style={{ fontSize: "0.9rem", marginBottom: "1rem" }}>
              Get your Client ID and Client Secret from{" "}
              <a href="https://developer.intuit.com" target="_blank" rel="noreferrer">developer.intuit.com</a>{" "}
              → your app → Keys &amp; Credentials. Each QuickBooks account needs its own Intuit Developer app.
            </p>
            {credMsg && <p className={credMsg === "Credentials saved." ? "msg-ok" : "msg-error"}>{credMsg}</p>}
            <div className="form-group">
              <label>Client ID</label>
              <input type="text" placeholder="Leave blank to keep existing" value={credForm.client_id}
                onChange={e => setCredForm(f => ({ ...f, client_id: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Client Secret</label>
              <input type="password" placeholder="Leave blank to keep existing" value={credForm.client_secret}
                onChange={e => setCredForm(f => ({ ...f, client_secret: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Redirect URI *</label>
              <input type="text" placeholder="https://your-app.railway.app/api/receipts/qbo-callback/"
                value={credForm.redirect_uri}
                onChange={e => setCredForm(f => ({ ...f, redirect_uri: e.target.value }))} />
              <small className="muted">Must match exactly what's registered in the Intuit Developer Portal → Settings → Redirect URIs.</small>
            </div>
            <div className="form-actions">
              <button className="btn-primary" disabled={credSaving} onClick={saveCredentials}>
                {credSaving ? "Saving..." : "Save"}
              </button>
              <button className="btn-secondary" onClick={() => setShowCredModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── View / edit / actions panel ── */}
      {viewingSubmission && (() => {
        const s = viewingSubmission;
        const result = matchResults[s.id];
        const BASE = process.env.REACT_APP_API_URL || "";
        return (
          <div className="modal-overlay" onClick={() => setViewingSubmission(null)}>
            <div className="modal-panel" onClick={e => e.stopPropagation()}>

              {/* Header */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1rem" }}>
                <div>
                  <h2 style={{ margin: 0 }}>Submission #{s.id}</h2>
                  <p className="muted" style={{ margin: "0.25rem 0 0" }}>
                    {s.submitted_by_name} · {s.date} ·{" "}
                    <span className="status-badge" style={{ background: STATUS_COLORS[s.status] }}>{STATUS_LABELS[s.status] || s.status}</span>
                    {s.qbo_purchase_id && <span className="muted" style={{ fontSize: 11, marginLeft: 8 }}>QBO: {s.qbo_purchase_id}</span>}
                  </p>
                  {s.source === "missing_receipts" && s.prelinked_purchase_id && (
                    <p className="muted" style={{ margin: "0.35rem 0 0", fontSize: 12 }}>
                      📌 Pre-matched to QBO #{s.prelinked_purchase_id} from Missing Receipts — no search needed.
                    </p>
                  )}
                </div>
                <button className="btn-link" onClick={() => setViewingSubmission(null)} style={{ fontSize: "1.4rem", lineHeight: 1 }}>×</button>
              </div>

              {/* Receipt file preview */}
              <div className="receipt-preview">
                {s.attachments && s.attachments.map(att => {
                  const url = BASE + att.file_url;
                  const isImage = att.content_type && att.content_type.startsWith("image/");
                  const isPdf = att.content_type === "application/pdf";
                  return (
                    <div key={att.id} className="receipt-preview-item">
                      {isImage && (
                        <div>
                          <img src={url} alt={att.filename} className="receipt-img-preview"
                            onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'inline'; }} />
                          <a href={url} target="_blank" rel="noreferrer" className="pdf-open-btn" style={{ display: 'none' }}>
                            🖼 Open image — {att.filename}
                          </a>
                        </div>
                      )}
                      {isPdf && (
                        <a href={url} target="_blank" rel="noreferrer" className="pdf-open-btn">
                          📄 Open PDF — {att.filename}
                        </a>
                      )}
                      {!isImage && !isPdf && (
                        <a href={url} target="_blank" rel="noreferrer" className="btn-secondary btn-sm">{att.filename}</a>
                      )}
                      <button className="btn-link" style={{ color: "#dc3545", marginLeft: "0.75rem" }}
                        onClick={() => {
                          if (!window.confirm("Delete this receipt file?")) return;
                          axios.delete(`${API}/admin/submissions/${s.id}/attachments/${att.id}/`, authHeader)
                            .then(() => {
                              const updated = { ...s, attachments: s.attachments.filter(a => a.id !== att.id) };
                              setViewingSubmission(updated);
                              setSubmissions(prev => prev.map(x => x.id === s.id ? updated : x));
                            })
                            .catch(console.error);
                        }}>
                        Remove
                      </button>
                    </div>
                  );
                })}
                {/* Upload replacement — auto-uploads on file select */}
                <div style={{ marginTop: "0.75rem" }}>
                  <input type="file" accept="image/*,.pdf"
                    disabled={uploadingFile}
                    onChange={e => {
                      const file = e.target.files[0];
                      if (!file) return;
                      setUploadingFile(true);
                      const fd = new FormData();
                      fd.append("receipt_file", file);
                      axios.post(`${API}/admin/submissions/${s.id}/attachments/`, fd, {
                        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
                      })
                        .then(res => {
                          const updated = { ...s, attachments: [...(s.attachments || []), res.data] };
                          setViewingSubmission(updated);
                          setSubmissions(prev => prev.map(x => x.id === s.id ? updated : x));
                        })
                        .catch(console.error)
                        .finally(() => { setUploadingFile(false); e.target.value = ""; });
                    }}
                    style={{ fontSize: "0.9rem" }} />
                  {uploadingFile && <span style={{ marginLeft: "0.5rem", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>Uploading…</span>}
                </div>
              </div>

              {/* Action buttons */}
              <div className="modal-actions">
                {s.status === "pending" && (
                  <>
                    <button className="btn-action approve" disabled={approving[s.id] || editSaving} onClick={() => handleApprove()}>
                      {approving[s.id] || editSaving ? "Working..." : "Approve"}
                    </button>
                    <button className="btn-action reject" title="Mark as rejected — stays in system, can be reset to pending" onClick={() => rejectSubmission(s.id)}>Reject</button>
                  </>
                )}
                {s.status === "submitted" && (
                  <button className="btn-action reset" onClick={handleUndo}>Undo Match</button>
                )}
                {s.status === "rejected" && (
                  <button className="btn-action reset" onClick={() => resetSubmission(s.id)}>Reset to Pending</button>
                )}
                <button className="btn-action reject" title="Permanently delete this submission from the database" onClick={() => deleteSubmission(s.id)}>Delete</button>
              </div>

              {/* Match result feedback */}
              {viewMatchResult && (
                <div className={`match-result ${viewMatchResult.match_status}`} style={{ marginBottom: "1rem" }}>
                  {(viewMatchResult.match_status === "matched" || viewMatchResult.match_status === "created") && (
                    <div>
                      <strong>{viewMatchResult.match_status === "matched" ? "✓ Matched successfully!" : "✓ Expense posted to QBO!"}</strong>
                      <table className="match-detail-table">
                        <tbody>
                          <tr><td>QBO Vendor</td><td>{viewMatchResult.qbo_vendor_name}</td></tr>
                          <tr><td>QBO Account</td><td>{viewMatchResult.qbo_account_name}</td></tr>
                          <tr><td>QBO Date</td><td>{viewMatchResult.qbo_payment_date}</td></tr>
                          <tr><td>QBO Amount</td><td>${parseFloat(viewMatchResult.qbo_total_amount || 0).toFixed(2)}</td></tr>
                          <tr><td>Purchase ID</td><td>{viewMatchResult.qbo_purchase_id}</td></tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                  {viewMatchResult.match_status === "no_match" && (
                    <div>
                      <p style={{ marginBottom: "0.5rem" }}>No matching QBO purchase found.</p>
                      <button
                        className="btn-action approve"
                        disabled={approving[viewingSubmission?.id]}
                        onClick={handleCreateExpense}
                      >
                        {approving[viewingSubmission?.id] ? "Posting..." : "Post Expense to QBO"}
                      </button>
                      <p className="muted" style={{ fontSize: "0.8rem", marginTop: "0.35rem" }}>
                        Posts a new expense to QBO with the receipt attached. No reference number — plain expense entry.
                      </p>
                    </div>
                  )}
                  {viewMatchResult.match_status === "prelink_confirm" && (
                    <div>
                      <p style={{ marginBottom: "0.5rem" }}>
                        {viewMatchResult.reason === "not_uncategorized"
                          ? "This QBO expense is no longer Uncategorized (8999) — it may have been matched or recategorized already."
                          : "This QBO expense doesn't match what was submitted."}
                        {" "}Review before proceeding — approving will overwrite QBO with the submitted line items.
                      </p>
                      <table className="match-detail-table">
                        <tbody>
                          <tr><td></td><td>In QuickBooks</td><td>Submitted</td></tr>
                          <tr><td>Vendor</td><td>{viewMatchResult.qbo_vendor_name || "—"}</td><td>{viewMatchResult.sub_vendor_name || "—"}</td></tr>
                          <tr><td>Amount</td><td>${parseFloat(viewMatchResult.qbo_total_amount || 0).toFixed(2)}</td><td>${parseFloat(viewMatchResult.sub_total_amount || 0).toFixed(2)}</td></tr>
                          <tr><td>Date</td><td>{viewMatchResult.qbo_payment_date}</td><td>{viewMatchResult.sub_payment_date}</td></tr>
                        </tbody>
                      </table>
                      <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem" }}>
                        <button className="btn-action approve" disabled={approving[viewingSubmission?.id]} onClick={() => handleApprove(null, true)}>
                          {approving[viewingSubmission?.id] ? "Working..." : "Approve Anyway"}
                        </button>
                      </div>
                      <p className="muted" style={{ fontSize: "0.8rem", marginTop: "0.35rem" }}>
                        Or edit the line items below and re-approve, reject this submission, or fix the expense directly in QuickBooks first.
                      </p>
                    </div>
                  )}
                  {viewMatchResult.match_status === "error" && <span>Error: {viewMatchResult.reason}</span>}
                  {viewMatchResult.match_status === "multiple_matches" && (
                    <span>
                      Multiple matches — pick one:&nbsp;
                      {viewMatchResult.purchases.map(p => (
                        <button key={p.id} className="btn-action approve" onClick={() => handleApprove(p.id)}>
                          {p.date} · ${parseFloat(p.amount).toFixed(2)} · {p.memo || "no memo"}
                        </button>
                      ))}
                    </span>
                  )}
                </div>
              )}

              {editMsg && <div className="alert alert-error">{editMsg}</div>}

              <hr style={{ margin: "1rem 0" }} />
              <h3 style={{ marginTop: 0 }}>Edit Details</h3>

              <div className="form-row">
                <div className="form-group">
                  <label>Vendor</label>
                  <select value={editForm.vendor} onChange={e => setEditForm(f => ({ ...f, vendor: e.target.value }))}>
                    <option value="">-- No vendor --</option>
                    {refVendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Date</label>
                  <input type="date" value={editForm.date} onChange={e => setEditForm(f => ({ ...f, date: e.target.value }))} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Account</label>
                  <select value={editForm.account} onChange={e => setEditForm(f => ({ ...f, account: e.target.value }))}>
                    <option value="">-- No account --</option>
                    {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Payment Type</label>
                  <select value={editForm.payment_type} onChange={e => setEditForm(f => ({ ...f, payment_type: e.target.value }))}>
                    <option value="Debit">Debit</option>
                    <option value="Credit Card">Credit Card</option>
                    <option value="Cash">Cash</option>
                    <option value="Check">Check</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Memo</label>
                <textarea rows={2} value={editForm.memo} onChange={e => setEditForm(f => ({ ...f, memo: e.target.value }))} />
              </div>

              <h3>Line Items</h3>
              {editLineItems.map((item, index) => (
                <div key={index} className="line-item-row">
                  <div className="line-item-fields">
                    <div className="form-group">
                      <label>Category</label>
                      <select value={item.category} onChange={e => handleEditCategoryChange(index, e.target.value)}>
                        <option value="">-- Category --</option>
                        {refCategories.map(c => <option key={c.id} value={c.id}>{displayName(c)}</option>)}
                      </select>
                    </div>
                    <div className="form-group flex-2">
                      <label>Description</label>
                      <input type="text" value={item.description} onChange={e => updateEditLineItem(index, "description", e.target.value)} />
                    </div>
                    <div className="form-group amount-field">
                      <label>Amount</label>
                      <input type="number" step="0.01" min="0" value={item.amount} onChange={e => updateEditLineItem(index, "amount", e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label>Ministry</label>
                      <select value={item.expense_class} onChange={e => updateEditLineItem(index, "expense_class", e.target.value)}>
                        <option value="">-- Ministry --</option>
                        {refClasses.map(c => <option key={c.id} value={c.id}>{displayName(c)}</option>)}
                      </select>
                    </div>
                  </div>
                  {editLineItems.length > 1 && (
                    <button type="button" className="btn-remove-line" onClick={() => setEditLineItems(prev => prev.filter((_, i) => i !== index))}>✕</button>
                  )}
                </div>
              ))}
              <button type="button" className="btn-secondary mb-2" onClick={() => setEditLineItems(prev => [...prev, { category: "", description: "", amount: "", expense_class: "" }])}>
                + Add Line Item
              </button>

              <div className="form-actions">
                <button className="btn-primary" disabled={editSaving} onClick={saveEdit}>
                  {editSaving ? "Saving..." : "Save"}
                </button>
                <button className="btn-secondary" onClick={() => setViewingSubmission(null)}>Close</button>
              </div>

            </div>
          </div>
        );
      })()}
    </div>
  );
}
